/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Cancel } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import EnvelopeWithBox from '../EnvelopeWithBox';
import { useCommunicationContext } from '../../contexts/CommunicationContext';
import useQidList from '../../common/hooks/useQidList';

import Styles from '../../css/CreateCommentForm.module.css';
import BtnStyles from '../../css/AutopayPage.module.css';
import { preventDefaultAndStopPropagation } from '../../common/UtilityFunctions';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import ButtonBorder from '../ButtonBorder';
import { Button } from '@mui/material';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import useFilteredResultCodesList from '../../common/hooks/useFilteredResultCodes';

function CreateCommentForm() {
  const { firstName, lastName, userId } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const { enqueueSnackbar } = useSnackbar();
  const today = useMemo(() => {
    const date = new Date();
    const year = date.getFullYear();

    let month: number | string = date.getMonth() + 1;
    let day: number | string = date.getDate();

    if (month < 10) month = '0' + month;
    if (day < 10) day = '0' + day;

    return `${year}-${month}-${day}`;
  }, []);

  const [formData, setFormData] = useState({
    addedByUid: userId, // 02/09/2026 - SQC-486, Justin Han-Nguyen - added this field to store the original commenter
    userUid: userId,
    addedByUserName: ((firstName || '') + ' ' + (lastName || '')).trim(), // 02/09/2026 - SQC-486, Justin Han-Nguyen - added this field to store the original commenter
    userName: ((firstName || '') + ' ' + (lastName || '')).trim(),
    actionCode: '',
    actionCodeDescription: '',
    actionCodeKeyword: '',
    codes: '',
    commentCategory: 'C',
    commentCategoryDescription: 'C - Collections Comment',
    commentCategoryKeyword: 'C',
    loanNumbers: `${loanNumber}`,
    loanNumber: '',
    text: '',
    promiseToPayDate: ``,
    promiseToPayAmt: '',
    resultCode: '',
    resultCodeDescription: '',
    resultCodeKeyword: '',
    success: '',
    validLoans: 0,
    invalidLoans: 0,
  });
  const [errorData, setErrorData] = useState({
    addedByUid: '', // 02/09/2026 - SQC-486, Justin Han-Nguyen - added this field to store the original commenter
    userUid: '',
    addedByUserName: '', // 02/09/2026 - SQC-486, Justin Han-Nguyen - added this field to store the original commenter
    userName: '',
    actionCode: '',
    actionCodeDescription: '',
    actionCodeKeyword: '',
    codes: '',
    commentCategory: '',
    commentCategoryDescription: '',
    commentCategoryKeyword: '',
    loanNumbers: '',
    loanNumber: '',
    text: '',
    promiseToPayDate: '',
    promiseToPayAmt: '',
    resultCode: '',
    resultCodeDescription: '',
    resultCodeKeyword: '',
    generic: '',
  });
  const [isBatchComment, setIsBatchComment] = useState(false);
  const toggleIsBatchComment = useCallback(() => setIsBatchComment((prev) => !prev), []);
  const [actionCodeDesc, setActionCodeDesc] = useState('');
  const [resultCodeDesc, setResultCodeDesc] = useState('');
  const [communicationNote, setCommunicationNote] = useState('');
  const loanNumberDigitRegex = /^[0-9\n]*$/;

  const categoriesResponse = useQidList('CMT4_CD.027');
  const categories = useMemo(() => categoriesResponse.qid, [categoriesResponse]);
  const loadingCategories = useMemo(() => categoriesResponse.loadingQid, [categoriesResponse]);
  const { isCommenting, createComment, setCreating } = useCommunicationContext();
  const actionCodesQidListResponse = useQidList('CMT4_CD.025');
  const actionCodes = useMemo(() => actionCodesQidListResponse.qid, [actionCodesQidListResponse]);
  const loadingActionCodes = useMemo(() => actionCodesQidListResponse.loadingQid, [actionCodesQidListResponse]);
  const resultCodesQidListResponse = useFilteredResultCodesList(formData.actionCode);
  const resultCodes = useMemo(() => resultCodesQidListResponse.filteredResultCodes, [resultCodesQidListResponse]);
  const loadingResultCodes = useMemo(
    () => resultCodesQidListResponse.loadingFilteredResultCodes,
    [resultCodesQidListResponse],
  );

  // Handle changes in the form, validate the data and set the state appropriately
  const handleFormChange = useCallback(
    (e) => {
      const { name, value } = e.currentTarget;
      switch (name) {
        case 'actionCode':
          const actionCode = actionCodes.find((v) => v.code === value) || { code: '', description: '', keyword: '' };
          setActionCodeDesc(
            actionCode.description.toString().split(/\s*-\s*/)[1] !== undefined
              ? actionCode.description.toString().split(/\s*-\s*/)[1]
              : actionCode.description.toString(),
          );
          setFormData((prev) => {
            if (value === 'PT') {
              return {
                ...prev,
                actionCode: `${value}`,
                actionCodeDescription: `${actionCode.description}`,
                actionCodeKeyword: `${actionCode.keyword}`,
                resultCode: '',
                resultCodeDescription: '',
                resultCodeKeyword: '',
                codes: actionCode.description + ', ',
                promiseToPayDate: today,
                promiseToPayAmt: '0.00',
              };
            }
            return {
              ...prev,
              actionCode: `${value}`,
              actionCodeDescription: `${actionCode.description}`,
              actionCodeKeyword: `${actionCode.keyword}`,
              codes: [actionCode.description, prev.resultCodeDescription].join(',') + ', ',
            };
          });
          break;
        case 'commentCategory':
          const commentCategory = categories.find((v) => v.code === value) || {
            code: '',
            description: '',
            keyword: '',
          };
          setFormData((prev) => {
            return {
              ...prev,
              commentCategory: `${value}`,
              commentCategoryDescription: `${commentCategory.description}`,
              commentCategoryKeyword: `${commentCategory.keyword}`,
            };
          });
          break;
        case 'loanNumber':
          if (formData.loanNumbers === null || formData.loanNumbers === '') {
            setFormData((prev) => {
              return {
                ...prev,
                loanNumbers: value,
              };
            });
            return;
          } else {
            setFormData((prev) => {
              return {
                ...prev,
                loanNumbers: loanNumberDigitRegex.test(value) ? value : prev.loanNumbers,
              };
            });
            return;
          }
        case 'removeLoanNumber':
          const loanNumber = e.currentTarget.getAttribute('data-loan-number');
          setFormData((prev) => {
            return {
              ...prev,
              loanNumbers: prev.loanNumbers
                .split(',')
                .filter((ln) => ln !== loanNumber)
                .join(','),
            };
          });
          break;
        case 'resultCode':
          const resultCode = resultCodes.find((v) => v.code === value) || { code: '', description: '', keyword: '' };
          setResultCodeDesc(
            resultCode.description.toString().split(/\s*-\s*/)[1] !== undefined
              ? resultCode.description.toString().split(/\s*-\s*/)[1]
              : resultCode.description.toString(),
          );
          setFormData((prev) => {
            return {
              ...prev,
              resultCode: value,
              resultCodeDescription: `${resultCode.description}`,
              resultCodeKeyword: `${resultCode.keyword}`,
              codes: [prev.actionCodeDescription, resultCode.description].join(', ') + ', ',
            };
          });
          break;
        default:
          setFormData((prev) => {
            return { ...prev, [name]: value };
          });
          break;
      }
    },
    [actionCodes, categories, resultCodes, today, communicationNote, formData.loanNumbers],
  );

  // Build the comment text from action code, result code, and communication note
  useEffect(() => {
    setFormData((prev) => {
      const commentText =
        actionCodeDesc === '' && resultCodeDesc === ''
          ? communicationNote
          : `${actionCodeDesc} - ${resultCodeDesc} ${communicationNote}`;
      return {
        ...prev,
        text: commentText,
      };
    });
  }, [actionCodeDesc, resultCodeDesc, communicationNote]);

  useEffect(() => {
    if (formData.success) {
      if (formData.validLoans && !formData.invalidLoans)
        enqueueSnackbar(`Successfully submitted comment for ${formData.validLoans} loans`, { variant: 'success' });
      else if (formData.validLoans && formData.invalidLoans)
        enqueueSnackbar(`${formData.invalidLoans} invalid loans numbers were detected and omitted`, {
          variant: 'warning',
        });
      else enqueueSnackbar('Unable to validate the loan numbers provided', { variant: 'error' });
      setCreating(false);
    }
  }, [formData.success, enqueueSnackbar]);

  return (
    <div className={Styles.centered}>
      <ButtonBorderWrapper>
        <EnvelopeWithBox blockName='Communication Details'>
          <form onSubmit={preventDefaultAndStopPropagation} className={Styles.form}>
            <label>
              <p>Action Code*</p>
              <select name='actionCode' value={formData.actionCode} onChange={handleFormChange} required>
                <option value=''>None</option>
                {loadingActionCodes ? (
                  <></>
                ) : (
                  actionCodes.map((actionCode) => (
                    <option key={actionCode.code} value={actionCode.code}>
                      {actionCode.description}
                    </option>
                  ))
                )}
              </select>
              {errorData.actionCode && <small>{errorData.actionCode}</small>}
            </label>
            <label>
              <p>Result Code*</p>
              <select name='resultCode' value={formData.resultCode} onChange={handleFormChange} required>
                <option value=''>None</option>
                {loadingResultCodes ? (
                  <></>
                ) : (
                  resultCodes.map((resultCode) => {
                    if (formData.actionCode === 'PT' && !`${resultCode.code || ''}`.startsWith('P'))
                      return <Fragment key={resultCode.code}></Fragment>;
                    return (
                      <option key={resultCode.code} value={resultCode.code}>
                        {resultCode.description}
                      </option>
                    );
                  })
                )}
              </select>
              {errorData.resultCode && <small>{errorData.resultCode}</small>}
            </label>
            <label>
              <p>Category*</p>
              <select name='commentCategory' value={formData.commentCategory} onChange={handleFormChange} required>
                <option value=''>None</option>
                {loadingCategories ? (
                  <></>
                ) : (
                  categories.map((category) => (
                    <option key={category.code} value={category.code}>
                      {category.description}
                    </option>
                  ))
                )}
              </select>
              {errorData.commentCategory && <small>{errorData.commentCategory}</small>}
            </label>
            <label>
              <p>Communication Note</p>
              <span>
                {actionCodeDesc} {actionCodeDesc && resultCodeDesc && '-'} {resultCodeDesc}
              </span>
              <textarea
                name='text'
                value={communicationNote}
                onChange={(e) => setCommunicationNote(e.target.value)}
                required
              />
              {errorData.text && <small>{errorData.text}</small>}
            </label>
            {formData.actionCode === 'PT' ? (
              <fieldset>
                <p>Promise to Pay</p>
                <label>
                  <input
                    name='promiseToPayDate'
                    type='date'
                    value={formData.promiseToPayDate}
                    onChange={handleFormChange}
                    required
                  />
                </label>
                <label>
                  <input
                    name='promiseToPayAmt'
                    type='number'
                    value={formData.promiseToPayAmt}
                    step={0.01}
                    onChange={handleFormChange}
                    placeholder='Amount $0.00'
                    required
                  />
                </label>
                {errorData.promiseToPayDate && <small>{errorData.promiseToPayDate}</small>}
                {errorData.promiseToPayAmt && <small>{errorData.promiseToPayAmt}</small>}
              </fieldset>
            ) : (
              <span />
            )}
            <label className={Styles.loans}>
              <p>
                Loan Numbers*: <span>{!isBatchComment && loanNumber}</span>
                {!isBatchComment && (
                  <button
                    type='button'
                    className={Styles.btn + ' ' + (isBatchComment ? Styles.checked : '')}
                    onClick={toggleIsBatchComment}
                  >
                    <div></div> Batch Comment<span className='sr-only'>Checked</span>
                  </button>
                )}
              </p>
              {isBatchComment && (
                <textarea
                  placeholder='XXXXXXXXXXXX'
                  name='loanNumber'
                  value={formData.loanNumbers || loanNumber}
                  onChange={handleFormChange}
                  cols={12}
                  className={Styles.batchList}
                  rows={10}
                />
              )}
              {errorData.loanNumber && <small>{errorData.loanNumber}</small>}
            </label>
            {errorData.loanNumber && <small>{errorData.loanNumber}</small>}
            <p>* Indicates required field</p>
            {errorData.generic && <small>{errorData.generic}</small>}
          </form>
        </EnvelopeWithBox>
        <ButtonBorder>
          <Button
            className={BtnStyles.button}
            type='button'
            disabled={isCommenting}
            onClick={(e) =>
              createComment(e, formData, actionCodes, categories, resultCodes, setErrorData, setFormData, true)
            }
          >
            {isCommenting ? 'Processing...' : 'Add Communication'}
          </Button>
          <Button
            className={BtnStyles.button}
            type='button'
            disabled={isCommenting}
            onClick={() => {
              setCreating(false);
            }}
            name='creating'
          >
            Cancel
          </Button>
        </ButtonBorder>
      </ButtonBorderWrapper>
    </div>
  );
}

export default CreateCommentForm;
